import { ensureConnectionsHasIds } from '@/adapter/applicationLoader';
import { Blocks } from '@/types/application';
import { Connection, IndexPortOption } from '@/types/connection';
import { MigrationReport } from '@/types/migration';
import { Prompt } from '@/types/prompt';
import { Subroutine } from '@/types/subroutine';
import { Variable, Variables } from '@/types/variable';
import { isArray, isNumber } from '../typeGuards';
import { reportChanges } from './report';

export const updateToFormat2 = (application: any, report: MigrationReport) => ({
  ...application,
  format: 2,
  subroutines: updateSubroutines(application.subroutines, report),
  variables: updateVariables(application.variables, report),
  blocks: updateBlocks(application.blocks, report),
  prompts: updatePrompts(application.prompts, report)
});

const updateSubroutines = (subroutines: Record<string, Subroutine>, report: MigrationReport): Subroutine[] => {
  if (subroutines.constructor === Array) return subroutines;

  const newSubroutines: Subroutine[] = [];
  Object.entries(subroutines).map(([subroutineId, subroutine]) => {
    newSubroutines.push({ ...subroutine, id: parseInt(subroutineId) });
  });

  reportChanges(report, 'Subroutines', 'keyed by id', 'a list, each carrying its id', newSubroutines.length);
  return newSubroutines;
};

const updateVariables = (variables: Record<string, Record<string, string>>, report: MigrationReport): Variables => {
  let convertedScopes = 0;
  let newVariables: Variables = {
    config: [],
    global: [],
    task: [],
    channel: [],
    session: [],
    local: {}
  };
  Object.entries(variables).map(([variableId, variable]) => {
    if (variableId !== 'local') {
      if (variable.constructor !== Array) {
        const newInnerVariables: Variable[] = [];
        Object.entries(variable).map(([innerVariableId, innerVariable]) => {
          newInnerVariables.push({ name: innerVariableId, type: innerVariable });
        });
        convertedScopes += 1;
        newVariables = { ...newVariables, [variableId]: newInnerVariables };
      } else {
        newVariables = { ...newVariables, [variableId]: variable };
      }
    } else {
      Object.entries(variable).map(([innerVariableId, innerVariable]) => {
        if (innerVariable.constructor !== Array) {
          const newInnerVariables: Variable[] = [];
          Object.entries(innerVariable).map(([localVariableName, localVariableType]) => {
            newInnerVariables.push({ name: localVariableName, type: localVariableType });
          });
          convertedScopes += 1;
          newVariables = { ...newVariables, local: { ...newVariables.local, [innerVariableId]: newInnerVariables } };
        } else {
          newVariables = { ...newVariables, local: { ...newVariables.local, [innerVariableId]: innerVariable } };
        }
      });
    }
  });

  reportChanges(report, 'Variable scopes', 'name keyed to type', 'a list of {name, type}', convertedScopes);
  return newVariables;
};

const updatePrompts = (prompts: Record<string, Prompt>, report: MigrationReport): Prompt[] => {
  const newPrompts: Prompt[] = [];
  if (prompts.constructor !== Array) {
    Object.entries(prompts).map(([promptId, prompt]) => {
      newPrompts.push({ ...prompt, id: parseInt(promptId) });
    });
  }

  reportChanges(report, 'Prompts', 'keyed by id', 'a list, each carrying its id', newPrompts.length);
  return newPrompts;
};

const updateBlocks = (blocks: Blocks, report: MigrationReport): Blocks => {
  const updatedConnections = updateConnections(blocks.connections, report);

  return {
    ...blocks,
    portoptions: updatedConnections.portOptions || {},
    connections: updatedConnections.connections
  };
};

const updateConnections = (connections: Record<string, object>, report: MigrationReport) => {
  let portOptions: Record<string, IndexPortOption> = {};
  let newConnections: Record<string, Connection[]> = {};
  let convertedBlocks = 0;
  let mintedIds = 0;

  Object.entries(connections).map(([connectionId, connection]) => {
    if (!isArray(connection)) {
      const newConnection: Connection[] = [];
      Object.entries(connection).map(([innerConnectionId, innerConnection]) => {
        if (innerConnectionId === '_index_port_option') {
          portOptions = { ...portOptions, [connectionId]: innerConnection };
        } else {
          newConnection.push({
            action: innerConnectionId,
            label: innerConnection.label,
            dst: innerConnection.dst,
            id: innerConnection.id
          });
        }
      });
      convertedBlocks += 1;
      mintedIds += newConnection.filter((entry) => !isNumber(entry.id)).length;
      newConnections = { ...newConnections, [connectionId]: ensureConnectionsHasIds(newConnection) };
    } else {
      mintedIds += connection.filter((entry) => !isNumber(entry.id)).length;
      newConnections = { ...newConnections, [connectionId]: ensureConnectionsHasIds(connection) };
    }
  });

  reportChanges(report, 'Connections', 'keyed by action', 'a list, each carrying its action', convertedBlocks);
  reportChanges(report, 'Port options', 'a connection entry', 'blocks.portoptions', Object.keys(portOptions).length);
  reportChanges(report, 'Connection ids', 'missing', 'minted for this session', mintedIds);

  return { portOptions: portOptions, connections: newConnections };
};
